/**
 * What to do when a resized block stops matching the task's estimate.
 *
 * Dragging a block's edge is how you say "this will take less than I thought"
 *, or "I can only give it an hour today". Those are different statements and
 * the app cannot tell them apart, so it asks instead of picking one silently.
 */

import { SNAP_MINUTES } from '@/lib/motion';
import { mergeIntervals, type MsInterval } from './scheduleConflict';

export type EstimateOutcome = 'match' | 'under' | 'over';

export interface EstimateDelta {
  outcome: EstimateOutcome;
  /** Minutes now on the calendar for this task, across every active block. */
  plannedMinutes: number;
  /** Unscheduled minutes when `under`; overshoot when `over`. Always positive. */
  deltaMinutes: number;
}

/**
 * Compare planned time against the estimate.
 *
 * The tolerance exists because a 15-minute grid produces small honest
 * mismatches all day; prompting for those would train the user to dismiss the
 * prompt without reading it.
 */
export function estimateDelta(
  estimateMinutes: number | null,
  plannedMinutes: number,
  { toleranceMinutes = SNAP_MINUTES }: { toleranceMinutes?: number } = {},
): EstimateDelta {
  if (estimateMinutes == null || estimateMinutes <= 0) {
    return { outcome: 'match', plannedMinutes, deltaMinutes: 0 };
  }

  const diff = estimateMinutes - plannedMinutes;
  if (Math.abs(diff) < toleranceMinutes) {
    return { outcome: 'match', plannedMinutes, deltaMinutes: 0 };
  }

  return {
    outcome: diff > 0 ? 'under' : 'over',
    plannedMinutes,
    deltaMinutes: Math.abs(diff),
  };
}

export interface FreeSlot {
  startsAt: Date;
  endsAt: Date;
}

const DEFAULT_SEARCH_MS = 7 * 24 * 60 * 60_000;

/**
 * First gap of at least `durationMs` starting at or after `after`.
 *
 * Whole-slot only: the remainder of a task goes somewhere it actually fits,
 * rather than being chopped again into pieces nobody asked for.
 */
export function findFreeSlot(
  after: Date,
  durationMs: number,
  occupied: MsInterval[],
  { searchMs = DEFAULT_SEARCH_MS }: { searchMs?: number } = {},
): FreeSlot | null {
  if (durationMs <= 0) return null;

  const startMs = after.getTime();
  const limit = startMs + searchMs;
  const merged = mergeIntervals(
    occupied.filter((o) => o.ends_at > startMs && o.starts_at < limit),
  );

  let cursor = startMs;
  while (cursor + durationMs <= limit) {
    const blocking = merged.find(
      (o) => o.starts_at < cursor + durationMs && o.ends_at > cursor,
    );
    if (!blocking) {
      return { startsAt: new Date(cursor), endsAt: new Date(cursor + durationMs) };
    }
    cursor = blocking.ends_at;
  }

  return null;
}
